import { content } from "@ctv/core/resource/payload";

import { ALPHABETH, ROMAN } from "./constants";
import {
  AlphabethSection,
  NumberSection,
  RomanSection,
  RomanTitleSection,
} from "./types";

export function constructNumberSection(
  sectionKey: string,
  cr: typeof content.CorporateTermsAndConditions,
  parentKey?: string
): NumberSection[] {
  const sectionName = sectionKey.replace("section", "");
  const maxNumber = findMaxNumberSection(sectionName, cr, parentKey);

  return Array.from({ length: maxNumber }).map((_, numberIndex) => {
    const currentKey = parentKey
      ? parentKey + (numberIndex + 1)
      : "num" + (numberIndex + 1);

    return {
      text: cr[(currentKey + sectionName) as keyof typeof cr],
      alphabethSection: constructAlphabethSection(currentKey, sectionName, cr),
    };
  });
}

function constructAlphabethSection(
  parentKey: string,
  sectionName: string,
  cr: typeof content.CorporateTermsAndConditions
): AlphabethSection[] {
  const maxAlphabeth = findMaxAlphabethSection(parentKey, sectionName, cr);

  return Array.from({
    length: maxAlphabeth,
  }).map<AlphabethSection>((_, alphabethIndex) => {
    const currentKey = parentKey + ALPHABETH[alphabethIndex];

    return {
      text: cr[(currentKey + sectionName) as keyof typeof cr],
      romanSections: constructRomanSection(currentKey, sectionName, cr),
    };
  });
}

function constructRomanSection(
  parentKey: string,
  sectionName: string,
  cr: typeof content.CorporateTermsAndConditions
): RomanSection[] {
  const maxRoman = findMaxRomanSection(parentKey, sectionName, cr);

  return Array.from({ length: maxRoman }).map((_, romanIndex) => {
    const currentKey = parentKey + "rom" + ROMAN[romanIndex];

    return {
      text: cr[(currentKey + sectionName) as keyof typeof cr],
    };
  });
}

export function constructRomanTitleSection(
  sectionKey: string,
  cr: typeof content.CorporateTermsAndConditions
): RomanTitleSection[] {
  const sectionName = sectionKey.replace("section", "");
  const maxRomanTitle = findMaxRomanTitleSection(sectionName, cr);

  return Array.from({ length: maxRomanTitle }).map((_, romanTitleIndex) => {
    const currentKey = "rom" + ROMAN[romanTitleIndex];

    return {
      title: cr[(currentKey + sectionName) as keyof typeof cr],
      numberSections: constructNumberSection(
        sectionName,
        cr,
        "num" + ROMAN[romanTitleIndex]
      ),
    };
  });
}

// Functions
function findMaxRomanTitleSection(
  section: string,
  cr: typeof content.CorporateTermsAndConditions
) {
  const items = Object.keys(cr)
    .filter((key) => key.includes(section))
    .map((key) => key.replace(section, ""));

  const romanTitleItems = items
    .filter((item) => /^romI+$/.test(item))
    .map((item) => item.match(/I+$/)?.[0])
    .filter(Boolean) as string[];

  return romanTitleItems.reduce((max, item) => {
    const index = ROMAN.indexOf(item);
    return Math.max(max, index === undefined ? 0 : index + 1);
  }, 0);
}

function findMaxNumberSection(
  section: string,
  cr: typeof content.CorporateTermsAndConditions,
  parentKey?: string
) {
  const parentRegex = new RegExp(`^${parentKey}\\d+${section}`);

  const items = Object.keys(cr)
    .filter((key) =>
      parentKey ? parentRegex.test(key) : key.includes(section)
    )
    .map((key) => key.replace(section, ""));

  const numberItems = items
    .map((item) => item.match(/\d+$/)?.[0])
    .filter(Boolean);

  return numberItems.reduce((max, item) => Math.max(max, Number(item)), 0);
}

function findMaxAlphabethSection(
  parentKey: string,
  section: string,
  cr: typeof content.CorporateTermsAndConditions
) {
  const parentRegex = new RegExp(`^${parentKey}[A-Z]${section}`);

  const items = Object.keys(cr)
    .filter((key) => parentRegex.test(key))
    .map((key) => key.replace(section, ""));

  const alphabethItems = items
    .map((item) => item.match(/[A-Z]+$/)?.[0])
    .filter(Boolean) as string[];

  return alphabethItems.reduce((max, item) => {
    const index = ALPHABETH.indexOf(item);
    return Math.max(max, index === undefined ? 0 : index + 1);
  }, 0);
}

function findMaxRomanSection(
  parentKey: string,
  section: string,
  cr: typeof content.CorporateTermsAndConditions
) {
  const parentRegex = new RegExp(`^${parentKey}romI+${section}`);

  const items = Object.keys(cr)
    .filter((key) => parentRegex.test(key))
    .map((key) => key.replace(section, ""));

  const romanItems = items
    .map((item) => item.match(/I+$/)?.[0])
    .filter(Boolean) as string[];

  return romanItems.reduce((max, item) => {
    const index = ROMAN.indexOf(item);
    return Math.max(max, index === undefined ? 0 : index + 1);
  }, 0);
}
